import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, updatePassword, createUserWithEmailAndPassword, signOut, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth, firebaseConfig } from '../../../services/firebase';

const SettingsManager = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    general: {
      siteName: 'Nirmitee Associates',
      siteDescription: 'Leading provider of construction, regulatory, and consulting services in Pune',
      contactEmail: 'nirmitee.associates2919@gmail.com',
      contactPhone: '+91 80039 89393',
      address: 'Pune, Maharashtra, India'
    },
    social: {
      facebook: 'https://facebook.com/nirmitee',
      twitter: 'https://twitter.com/nirmitee',
      linkedin: 'https://linkedin.com/company/nirmitee',
      instagram: 'https://instagram.com/nirmitee'
    },
    navigation: {
      showServices: true,
      showCalculators: true,
      showBlog: true,
      showTeam: true,
      showTestimonials: true
    },
    appearance: {
      primaryColor: '#2563eb',
      secondaryColor: '#64748b',
      logo: '/assets/images/logo.png'
    }
  });

  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [userForm, setUserForm] = useState({ email: '', password: '' });
  const [securityStatus, setSecurityStatus] = useState({ type: '', message: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
    modal: false
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setSecurityStatus({ type: '', message: '' });

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      return setSecurityStatus({ type: 'error', message: 'Passwords do not match' });
    }
    if (passwordForm.newPassword.length < 6) {
      return setSecurityStatus({ type: 'error', message: 'Password must be at least 6 characters' });
    }

    try {
      setIsProcessing(true);
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, passwordForm.currentPassword);
      
      // Re-authenticate first
      await reauthenticateWithCredential(user, credential);
      
      await updatePassword(user, passwordForm.newPassword);
      
      setSecurityStatus({ type: 'success', message: 'Password updated successfully. Logging you out...' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswords(prev => ({ ...prev, current: false, new: false, confirm: false }));
      
      // Logout the user after short delay to show success message
      setTimeout(() => {
        signOut(auth);
      }, 1500);
    } catch (error) {
      if (error.code === 'auth/invalid-credential') {
        setSecurityStatus({ type: 'error', message: 'Current password does not match.' });
      } else {
        setSecurityStatus({ type: 'error', message: error.message });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSecurityStatus({ type: '', message: '' });

    if (userForm.password.length < 6) {
      return setSecurityStatus({ type: 'error', message: 'Password must be at least 6 characters' });
    }

    try {
      setIsProcessing(true);
      
      // Initialize secondary app to avoid logging out the current admin
      const secondaryApp = initializeApp(firebaseConfig, 'SecondaryApp');
      const secondaryAuth = getAuth(secondaryApp);

      await createUserWithEmailAndPassword(secondaryAuth, userForm.email, userForm.password);
      
      // Sign out the secondary app immediately and delete it to clean up
      await signOut(secondaryAuth);
      await deleteApp(secondaryApp);

      setSecurityStatus({ type: 'success', message: `User ${userForm.email} created successfully.` });
      setUserForm({ email: '', password: '' });
      setIsAddUserModalOpen(false);
      setShowPasswords(prev => ({ ...prev, modal: false }));
    } catch (error) {
      setSecurityStatus({ type: 'error', message: error.message });
    } finally {
      setIsProcessing(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: '⚙️' },
    { id: 'security', label: 'Security', icon: '🔒' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Site Settings</h2>
        <p className="text-gray-600">Configure website settings and preferences</p>
      </div>

      <div className="flex space-x-6">
        {/* Sidebar */}
        <div className="w-64 bg-white border border-gray-200 rounded-lg p-4">
          <nav className="space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors duration-200 ${activeTab === tab.id
                  ? 'bg-custom-brown/10 text-custom-brown'
                  : 'text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-6">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">General Settings</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Site Name
                  </label>
                  <input
                    type="text"
                    defaultValue={settings.general.siteName}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    defaultValue={settings.general.contactEmail}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    defaultValue={settings.general.contactPhone}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    defaultValue={settings.general.address}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site Description
                </label>
                <textarea
                  rows="3"
                  defaultValue={settings.general.siteDescription}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown transition-colors"
                />
              </div>
            </div>
          )}


          {activeTab === 'security' && (
            <div className="space-y-8">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Security Settings</h3>
                  <div className="text-sm bg-custom-brown/10 text-custom-brown px-3 py-1 rounded-full border border-custom-brown/20">
                    Logged in as: <span className="font-semibold">{auth.currentUser?.email}</span>
                  </div>
                </div>
                {securityStatus.message && (
                  <div className={`p-4 rounded-md mb-6 ${securityStatus.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-green-50 text-green-800 border border-green-200'}`}>
                    {securityStatus.message}
                  </div>
                )}
                
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-8">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Change Password</h4>
                  <form onSubmit={handlePasswordChange} className="space-y-4" autoComplete="off">
                    {/* Fake inputs to trick Chrome's password manager */}
                    <input style={{ display: 'none' }} type="text" name="fakeusernameremembered" />
                    <input style={{ display: 'none' }} type="password" name="fakepasswordremembered" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                        <div className="relative">
                          <input
                            type={showPasswords.current ? "text" : "password"}
                            required
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown pr-10"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('current')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                        <div className="relative">
                          <input
                            type={showPasswords.new ? "text" : "password"}
                            required
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown pr-10"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('new')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                        <div className="relative">
                          <input
                            type={showPasswords.confirm ? "text" : "password"}
                            required
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown pr-10"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => togglePasswordVisibility('confirm')}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                          >
                            {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <button 
                      type="submit" 
                      disabled={isProcessing}
                      className="bg-custom-brown text-white px-4 py-2 rounded hover:bg-custom-brown/90 transition-colors disabled:opacity-50"
                    >
                      {isProcessing ? 'Processing...' : 'Update Password'}
                    </button>
                  </form>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-md font-medium text-gray-900 mb-1">Admin Users</h4>
                      <p className="text-sm text-gray-500">Manage administrator accounts for this panel.</p>
                    </div>
                    <button 
                      onClick={() => setIsAddUserModalOpen(true)}
                      className="bg-gray-900 text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors"
                    >
                      + Add New Admin
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'general' && (
            <div className="pt-6 border-t border-gray-200 mt-6">
              <div className="flex justify-end space-x-3">
                <button className="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200 transition-colors duration-200">
                  Reset to Defaults
                </button>
                <button className="bg-custom-brown text-white px-4 py-2 rounded hover:bg-custom-brown/90 transition-colors duration-200">
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      {isAddUserModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Add New Admin User</h3>
              <button 
                onClick={() => setIsAddUserModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-6">
                Create a new administrator account. They will be able to log in immediately with these credentials.
              </p>
              
              <form onSubmit={handleCreateUser} className="space-y-4" autoComplete="off">
                {/* Fake inputs to trick Chrome's password manager */}
                <input style={{ display: 'none' }} type="text" name="fakeusernameremembered2" />
                <input style={{ display: 'none' }} type="password" name="fakepasswordremembered2" />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    required
                    value={userForm.email}
                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown"
                    placeholder="admin@example.com"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Temporary Password</label>
                  <div className="relative">
                    <input
                      type={showPasswords.modal ? "text" : "password"}
                      required
                      value={userForm.password}
                      onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:ring-custom-brown focus:border-custom-brown pr-10"
                      placeholder="Min. 6 characters"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('modal')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.modal ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <div className="pt-4 flex justify-end space-x-3">
                  <button 
                    type="button"
                    onClick={() => setIsAddUserModalOpen(false)}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={isProcessing}
                    className="bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {isProcessing ? 'Creating...' : 'Create Admin'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsManager; 